import { Stack } from "@mui/material";
import type { FC } from "react";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { EmptyTimelineState } from "web_myplann/NotificationCenter/Components/EmptyTimelineState";
import { ResolvingNowSection } from "web_myplann/NotificationCenter/Components/ResolvingNowSection";
import { TimelineGroup } from "web_myplann/NotificationCenter/Components/TimelineGroup";
import { TimelineSkeleton } from "web_myplann/NotificationCenter/Components/TimelineSkeleton";
import { useNavigateFromResolvingNowItem } from "web_myplann/NotificationCenter/Hooks/useNavigateFromResolvingNowItem";
import { useNavigateFromTimelineItem } from "web_myplann/NotificationCenter/Hooks/useNavigateFromTimelineItem";
import { useUpcomingTimelineGroups } from "web_myplann/NotificationCenter/Hooks/useUpcomingTimelineGroups";
import { EmptyTimelineVariant } from "web_myplann/NotificationCenter/Types/EmptyTimelineVariant";
import { NOTIFICATION_CENTER_ORIGIN } from "web_myplann/NotificationCenter/Utils/notificationOrigin";
import NotificationCenterError from "./NotificationCenterError";

export const UpcomingTab: FC = () => {
	const { t } = useResource();
	const { upcomingQuery, resolvingNowQuery, resolvingNow, timeGroups } =
		useUpcomingTimelineGroups();
	const navigateToItem = useNavigateFromTimelineItem({
		origin: NOTIFICATION_CENTER_ORIGIN,
	});
	const navigateToResolvingItem = useNavigateFromResolvingNowItem({
		origin: NOTIFICATION_CENTER_ORIGIN,
	});

	const isLoading =
		(upcomingQuery.isLoading && !upcomingQuery.data) ||
		(resolvingNowQuery.isLoading && !resolvingNowQuery.data);
	const isError = upcomingQuery.isError || resolvingNowQuery.isError;

	if (isLoading) {
		return (
			<TimelineSkeleton
				description={t(Resources.NotificationCenter.Tabs.Upcoming)}
			/>
		);
	}

	if (isError) {
		const handleRetry = () => {
			void upcomingQuery.refetch();
			void resolvingNowQuery.refetch();
		};

		return <NotificationCenterError handleRetry={handleRetry} />;
	}

	const isEmpty = resolvingNow.length === 0 && timeGroups.length === 0;
	if (isEmpty) {
		return <EmptyTimelineState variant={EmptyTimelineVariant.Upcoming} />;
	}

	return (
		<Stack gap={0} data-testid="notification-center-upcoming-content">
			<ResolvingNowSection
				items={resolvingNow}
				onItemClick={navigateToResolvingItem}
			/>
			{timeGroups.map((group) => (
				<TimelineGroup
					key={group.bucketKey}
					group={group}
					onItemClick={navigateToItem}
				/>
			))}
		</Stack>
	);
};
